
import { HttpClientModule } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule, Routes } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { ImageCropperModule } from 'ngx-image-cropper';

import { AppComponent } from './app.component';
import { routing } from './app.routing';

//Layouts
import { SignupLayoutComponent } from './layouts/signup-layout/signup-layout.component';
import { ErrorLayoutComponent } from './layouts/error-layout/error-layout.component';
import { PublicLayoutComponent } from './layouts/public-layout/public-layout.component';
import { SecureLayoutComponent } from './layouts/secure-layout/secure-layout.component';

//Shared
import { FooterComponent } from './components/shared/footer/footer.component';
import { ModalComponent } from './components/shared/modal/modal.component';
import { AccountMenuComponent } from './components/shared/account-menu.component';

//Controls
import { UpcomingSessionCardComponent } from './components/controls/upcoming-session-card/upcoming-session-card.component';
import { DateInputControlComponent } from './components/controls/date-input-control/date-input-control.component';

//Components
import { HomeComponent } from './components/home/home.component';
import { PageNotFoundComponent } from '../app/components/error/pagenotfound/pagenotfound.component';

//Professionals
import { SearchComponent } from './components/professionals/pro-search/search.component';
import { ProProfileComponent } from './components/professionals/pro-profile/pro-profile.component';
import { SearchResultsComponent } from './components/professionals/pro-search-results/search-results.component';
import { RedeemSessionComponent } from './components/account/redeemsession/redeemsession.component';
import { RedeemSessionListComponent } from './components/controls/redeem-session-list/redeem-session-list.component';
import { ProProfileInfoComponent } from './components/professionals/profile-info/pro-profile-info.component';
import { ProProfileBookingComponent } from './components/professionals/profile-booking/pro-profile-booking.component';

//Auth
import { LoginComponent } from '../app/components/auth/login/login.component';
import { ForgottenComponent } from './components/auth/forgotten/forgotten.component';
import { ResetPasswordComponent } from './components/auth/forgotten/resetpwd.component';
import { ResetPwdSuccessComponent } from './components/auth/forgotten/resetpwdsuccess.component';
import { UnlockAccountComponent } from './components/auth/unlock/unlockaccount.component';

//Signup
import { Step1Component } from '../app/components/signup/1-signup/step1.component';
import { Step2Component } from '../app/components/signup/2-clientassessment/step2.component';
import { Step3Component } from '../app/components/signup/3-clientprofile/step3.component';
import { Step4Component } from '../app/components/signup/4-accountverification/step4.component';
import { Step5Component } from '../app/components/signup/5-signupcomplete/step5.component';
import { Step2FailComponent } from './components/signup/2-clientassessment/stepfail.component';

//Account
import { ConfirmSessionComponent } from './components/account/confirmsession/confirmsession.component';
import { MessagesComponent } from './components/account/messages/messages.component';
import { MessageViewComponent } from './components/account/messageview/messageview.component';
import { MessageWriteComponent } from './components/account/messagewrite/messagewrite.component';
import { ProfileComponent } from './components/account/profile/profile.component';
import { SessionBookingConfirmedComponent } from './components/account/sessionconfirmed/sessionconfirmed.component';
import { SessionPaymentComponent } from './components/account/sessionpayment/sessionpayment.component';
import { B2CSessionPaymentComponent } from './components/account/sessionpayment/b2c-sessionpayment.component';
import { B2BSessionPaymentComponent } from './components/account/sessionpayment/b2b-sessionpayment.component';
import { SessionsComponent } from './components/account/sessions/sessions.component';
import { SessionTakenComponent } from './components/account/sessiontaken/sessiontaken.component';
import { SettingsComponent } from './components/account/settings/settings.component';
import { ProfileImageComponent } from './components/account/profile/profile-image.component';
import { RedeemSessionService } from './services/redeemsession.service';
import { OnDemandComponent } from './components/account/ondemand/ondemand.component';

//Services
import { SignupService } from './services/signup.service';
import { AuthService } from './services/auth.service';
import { ReferenceDataService } from './services/referencedata.service';
import { CounsellorService } from './services/counsellor.service';
import { SearchService } from './services/search.service';

//Resolves
import { CounsellingTopicResolve } from './resolvers/counsellingtopic.resolve';
import { ReasonForMedicationResolve } from './resolvers/reasonformedication.resolve';
import { CountryResolve } from './resolvers/country.resolve';
import { CallingCodeResolve } from './resolvers/callingcode.resolve';
import { StateResolve } from './resolvers/state.resolve';
import { NationalityResolve } from './resolvers/nationality.resolve';
import { OrgIDResolve } from './resolvers/orgid.resolve';
import { GlobalSettingsResolve } from './resolvers/globalsettings.resolve';
import { WhoAmIResolve } from './resolvers/whoami.resolve';
import { AccountService } from './services/account.service';
import { AccountDataResolve } from './resolvers/accountdata.resolve';
import { LanguageResolve } from './resolvers/language.resolve';
import { CounsellorProfileResolve } from './resolvers/counsellorprofile.resolve';
import { SessionCreditResolve } from './resolvers/session-credit.resolve';

//Guards
import { SignupGuard } from './guards/signup.guard';
import { AuthGuard } from './guards/auth.guard';

//Pipes
import { NewlinePipe } from './pipes/formatnewlines.pipe';
import { DurationPipe } from './pipes/duration.pipe';
import { SessionTypePipe } from './pipes/sessiontype.pipe';
import { SessionClientDatePipe } from './pipes/session-client-date.pipe';
import { SessionClientTimePipe } from './pipes/session-client-time.pipe';

//Modules
import { NgxStripeModule } from 'ngx-stripe';
import { environment } from '../environments/environment';
import { OnDemandService } from './services/ondemand.service';


@NgModule({
  declarations: [
    AppComponent,
    SecureLayoutComponent,
    SignupLayoutComponent,
    ErrorLayoutComponent,
    PublicLayoutComponent,
    HomeComponent,
    PageNotFoundComponent,
    LoginComponent,
    ForgottenComponent,
    Step1Component,
    Step2Component,
    Step3Component,
    Step4Component,
    Step5Component,
    Step2FailComponent,
    AccountMenuComponent,
    ProfileComponent,
    MessagesComponent,
    MessageViewComponent,
    MessageWriteComponent,
    OnDemandComponent,
    SettingsComponent,
    NewlinePipe,
    ResetPasswordComponent,
    ResetPwdSuccessComponent,
    SearchComponent,
    SearchResultsComponent,
    ProProfileComponent,
    ProProfileInfoComponent,
    ProProfileBookingComponent,
    ConfirmSessionComponent,
    SessionTypePipe,
    DurationPipe,
    SessionsComponent,
    SessionBookingConfirmedComponent,
    SessionTakenComponent,
    SessionPaymentComponent,
    UnlockAccountComponent,
    FooterComponent,
    ModalComponent,
    UpcomingSessionCardComponent,
    SessionClientDatePipe,
    SessionClientTimePipe,
    DateInputControlComponent,
    B2BSessionPaymentComponent,
    B2CSessionPaymentComponent,
    ProfileImageComponent,
    RedeemSessionComponent,
    RedeemSessionListComponent
  ],
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserModule.withServerTransition({ appId: 'ng-cli-universal' }),
    HttpClientModule,
    ImageCropperModule,
    routing,
    NgxStripeModule.forRoot(environment.stripeKey),//pk_live_oc4And3zKrzLmnFU43h5REIf 
  ],
  providers: [
    AuthService,
    AccountService,
    OnDemandService,
    SignupService,
    ReferenceDataService,
    CounsellingTopicResolve,
    ReasonForMedicationResolve,
    SignupGuard,
    AuthGuard,
    CountryResolve,
    CallingCodeResolve,
    StateResolve,
    NationalityResolve,
    OrgIDResolve,
    GlobalSettingsResolve,
    WhoAmIResolve,
    AccountDataResolve,
    LanguageResolve,
    CounsellorService,
    CounsellorProfileResolve,
    SessionCreditResolve,
    RedeemSessionService,
    SearchService],
  bootstrap: [AppComponent]
})
export class AppModule { }
